import { FocusEvent, useEffect, useMemo, useState } from 'react';
import { FormikErrors, FormikTouched, getIn, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { FirstNameInput } from './first-name-input/first-name-input';
import { useSelector } from 'react-redux';
import { getUseSuffixInFullName } from '@redux/selectors/verification-configuration-selectors';
import { MiddleNameInput } from './middle-name-input/middle-name-input';
import { LastNameInput } from './last-name-input/last-name-input';
import { SuffixInput } from './suffix-input/suffix-input';
import { getFullNameValue } from '@utils/attribute-data-util';
import classNames from "classnames";
import styles from "./full-name-input-container.less";

type FullNameInputContainerProps = {
	title: string;
	attribute: AttributeFormState;
	fieldName: string;
	fieldTouched: boolean;
	fieldError: string;
	fieldIsInvalid: boolean;
	fieldShouldBeHighlighted: boolean;
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	onBlur: (event: FocusEvent<HTMLInputElement>) => void;
	onSetSubmitValue: (data: any) => void;
	isSubmitting: boolean;
};

export const FullNameInputContainer = (props: FullNameInputContainerProps) => {
	const {
		attribute,
		fieldName,
		setFieldValue,
		onBlur,
		onSetSubmitValue,
		isSubmitting
	} = props;

	const useSuffix = useSelector(getUseSuffixInFullName);

	const isRequired = useMemo(() => !(attribute.isOptional || attribute.optional), [attribute.isOptional, attribute.optional]);
	const FullNameSchema = isRequired
		? Yup.object().shape({
			fname: Yup.string()
				.min(1, 'validation.fname.min')
				.max(50, 'validation.fname.max')
				.matches(/^\S.*$/, 'validation.fname.required')
				.required('validation.fname.required'),
			lname: Yup.string()
				.min(1, 'validation.lname.min')
				.max(50, 'validation.lname.max')
				.matches(/^\S.*$/, 'validation.lname.required')
				.required('validation.lname.required'),
			suffix: Yup.string().max(12, 'validation.suffix.max')
		})
		: null;

	const [formInitialValue] = useState(attribute.inputData);

	useEffect(() => {
		const { fname, mname, lname } = attribute.inputData;
		if (fname && lname && fname.length > 0 && lname.length > 0) {
			const fullName = getFullNameValue(fname, mname, lname);
			setFieldValue(fieldName, fullName);
		} else {
			setFieldValue(fieldName, "");
		}
	}, [attribute.inputData.fname, attribute.inputData.mname, attribute.inputData.lname, attribute.inputData.suffix]);

	if (!attribute.isNew) {
		return null;
	}

	const renderFullNameInputContainer = (
		errors: FormikErrors<any>,
		touched: FormikTouched<any>,
		setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
		setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>
	) => {
		const firstNameFieldTouched = getIn(touched, "fname");
		const firstNameFieldError = getIn(errors, "fname");
		const middleNameFieldTouched = getIn(touched, "mname");
		const middleNameFieldError = getIn(errors, "mname");
		const lastNameFieldTouched = getIn(touched, "lname");
		const lastNameFieldError = getIn(errors, "lname");
		const suffixFieldTouched = getIn(touched, "suffix");
		const suffixFieldError = getIn(errors, "suffix");

		return (
			<div className={classNames(styles.fullNameInputContainer, useSuffix ? styles.withSuffix : styles.withoutSuffix)}>
				<FirstNameInput
					attribute={attribute}
					fieldTouched={firstNameFieldTouched}
					fieldError={firstNameFieldError}
					onBlur={onBlur}
					setFieldTouched={setFieldTouched}
					setFieldValue={setFieldValue}
					isSubmitting={isSubmitting}
					onSetSubmitValue={onSetSubmitValue}
				/>
				<MiddleNameInput
					attribute={attribute}
					fieldError={middleNameFieldError}
					fieldTouched={middleNameFieldTouched}
					onBlur={onBlur}
					setFieldTouched={setFieldTouched}
					setFieldValue={setFieldValue}
					isSubmitting={isSubmitting}
					onSetSubmitValue={onSetSubmitValue}
				/>
				<LastNameInput
					attribute={attribute}
					fieldError={lastNameFieldError}
					fieldTouched={lastNameFieldTouched}
					onBlur={onBlur}
					setFieldTouched={setFieldTouched}
					setFieldValue={setFieldValue}
					isSubmitting={isSubmitting}
					onSetSubmitValue={onSetSubmitValue}
				/>
				{useSuffix && (
					<SuffixInput
						attribute={attribute}
						fieldError={suffixFieldError}
						fieldTouched={suffixFieldTouched}
						onBlur={onBlur}
						setFieldTouched={setFieldTouched}
						setFieldValue={setFieldValue}
						isSubmitting={isSubmitting}
						onSetSubmitValue={onSetSubmitValue}
					/>
				)}
			</div>
		)
	}

	return (
		<Formik
			initialValues={formInitialValue}
			validateOnMount={true}
			validationSchema={FullNameSchema}
			onSubmit={undefined}
		>
			{({ errors, touched, setFieldTouched, setFieldValue }: FormikProps<any>) => (
				renderFullNameInputContainer(
					errors,
					touched,
					setFieldTouched,
					setFieldValue
				)
			)}
		</Formik>
	);
};